/**
 * @fileoverview Returns/cancellations indicator icon with tooltip.
 * @module shared/components/ui/ReturnsIndicator
 */

import { ReactElement } from 'react'
import { FlagTriangleRight, Undo2 } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useCompactMoney } from '@/shared/hooks/useCompactMoney'
import { cn } from '@/shared/utils/cn'

/**
 * Props for the ReturnsIndicator component.
 */
export interface ReturnsIndicatorProps {
  /** Number of returned/cancelled items */
  quantity: number
  /** Potential revenue loss amount */
  potentialRevenueLoss: number
  /** Additional CSS classes */
  className?: string
}

/**
 * Small indicator icon showing returns/cancellations with a tooltip.
 * Displays a dim flag icon that reveals quantity and potential revenue loss on hover.
 *
 * @component
 * @example
 * ```tsx
 * <ReturnsIndicator quantity={5} potentialRevenueLoss={250} />
 * ```
 */
export function ReturnsIndicator({
  quantity,
  potentialRevenueLoss,
  className,
}: ReturnsIndicatorProps): ReactElement {
  const compactMoney = useCompactMoney()

  const formatted = compactMoney(Math.abs(potentialRevenueLoss))
  const valueMatch = formatted.match(/^([+\-]?[\d.,]+[KMB]?)\s*(.*)$/)
  const numericPart = valueMatch?.[1] ?? formatted
  const currencyPart = valueMatch?.[2] ?? ''

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-center',
            className
          )}
        >
          <FlagTriangleRight className="size-3.5 text-[var(--color-negative-secondary)] opacity-60" />
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        hideArrow
        className="rounded-lg border border-border/50 !bg-[oklch(from_var(--glass-surface-dark)_l_c_h/0.95)] p-3 shadow-xl"
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-sm text-foreground">
            <Undo2 className="size-4" />
            <span className="font-medium">{quantity}× Refunds</span>
          </div>
          <p className="whitespace-nowrap">
            <span className="text-2xl font-semibold tracking-tight font-mono text-negative">
              -{numericPart}
            </span>
            {currencyPart && (
              <span className="text-xl font-medium ml-1 text-negative">
                {currencyPart}
              </span>
            )}
          </p>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
